import { create } from 'zustand';

interface TimerState {
  /** Whether the timer is currently ticking. */
  isRunning: boolean;
  /** Elapsed seconds since the timer was started. Updated by tick(). */
  elapsedSeconds: number;
  /** The task being timed. */
  taskId: string | null;
  /** Cached task title for display purposes. */
  taskTitle: string | null;
  /** Cached project name for display purposes. */
  projectName: string | null;

  /** Start timing a task. Resets elapsed time. */
  startTimer: (taskId: string, taskTitle: string, projectName?: string) => void;
  /** Stop the timer and return the elapsed data, or null if not running. */
  stopTimer: () => { taskId: string; elapsedSeconds: number; hours: number } | null;
  /** Called every second while the timer is running to update elapsedSeconds. */
  tick: () => void;
}

// Module-level ref for drift‑free timing — shared across all store subscribers.
let startTimeRef: number | null = null;

export const useTimerStore = create<TimerState>((set, get) => ({
  isRunning: false,
  elapsedSeconds: 0,
  taskId: null,
  taskTitle: null,
  projectName: null,

  startTimer(taskId, taskTitle, projectName) {
    startTimeRef = Date.now();
    set({
      isRunning: true,
      elapsedSeconds: 0,
      taskId,
      taskTitle,
      projectName: projectName || null,
    });
  },

  stopTimer() {
    const state = get();
    if (!state.isRunning || !state.taskId || startTimeRef === null) return null;

    const finalSeconds = Math.floor((Date.now() - startTimeRef) / 1000);
    const hours = Math.max(0.01, Math.round((finalSeconds / 3600) * 100) / 100);

    startTimeRef = null;
    set({
      isRunning: false,
      elapsedSeconds: 0,
      taskId: null,
      taskTitle: null,
      projectName: null,
    });

    return { taskId: state.taskId, elapsedSeconds: finalSeconds, hours };
  },

  tick() {
    if (startTimeRef !== null) {
      set({ elapsedSeconds: Math.floor((Date.now() - startTimeRef) / 1000) });
    }
  },
}));
